export interface HeroContent {
  badge?: string;
  headline: string;
  subheadline?: string;
  description?: string;
  primaryCTA: { text: string; href: string };
  secondaryCTA?: { text: string; href: string };
  backgroundImage: string;
  stats?: { value: string; label: string }[];
}

interface Props {
  content: HeroContent;
}

export default function HeroSection({ content }: Props) {
  const { badge, headline, subheadline, description, primaryCTA, secondaryCTA, backgroundImage, stats } = content;

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-[#0A0806]">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="One Call Handyman Service Houston TX luxury home renovation"
          className="w-full h-full object-cover object-center"
        />
        {/* Stronger layered overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/88 via-black/72 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0806] via-black/20 to-black/40" />
        <div className="absolute inset-0 bg-black/25" />
      </div>

      {/* Vertical gold accent line — left edge, decorative */}
      <div className="absolute left-8 top-[20%] bottom-[25%] w-px bg-gradient-to-b from-transparent via-[#BF9448]/60 to-transparent hidden xl:block" />

      {/* Main content area */}
      <div className="flex-1 flex items-center relative z-10">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-16">
          <div className="max-w-2xl xl:max-w-3xl">
            {badge && (
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-px bg-[#BF9448]" />
                <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
                  {badge}
                </span>
              </div>
            )}

            <h1
              className="font-serif font-semibold text-white leading-[1.08] mb-6"
              style={{ fontSize: 'clamp(2.6rem, 5vw, 4.5rem)', textShadow: '0 4px 24px rgba(0,0,0,0.6)' }}
            >
              {headline}
            </h1>

            {subheadline && (
              <p
                className="font-serif text-[#BF9448] font-medium mb-5"
                style={{ fontSize: 'clamp(1.1rem, 2vw, 1.5rem)' }}
              >
                {subheadline}
              </p>
            )}

            {description && (
              <p className="font-sans text-white/60 text-base md:text-lg leading-relaxed mb-12 max-w-xl">
                {description}
              </p>
            )}

            <div className="flex flex-wrap gap-4">
              <a
                href={primaryCTA.href}
                className="bg-[#BF9448] hover:bg-[#D4AB5E] text-white font-sans font-semibold px-9 py-4 rounded-sm transition-all duration-300 whitespace-nowrap cursor-pointer text-sm tracking-[0.12em] uppercase"
              >
                {primaryCTA.text}
              </a>
              {secondaryCTA && (
                <a
                  href={secondaryCTA.href}
                  className="group flex items-center gap-3 border border-white/25 hover:border-[#BF9448]/80 text-white font-sans font-medium px-9 py-4 rounded-sm transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-fill text-[#BF9448] text-base"></i>
                  </span>
                  {secondaryCTA.text}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats bar — anchored to bottom */}
      {stats && stats.length > 0 && (
        <div className="relative z-10 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-7">
            <div className="flex flex-wrap items-center gap-10 lg:gap-16">
              {stats.map((stat, i) => (
                <div key={i} className="flex items-center gap-4">
                  {i > 0 && (
                    <div className="hidden lg:block w-px h-8 bg-white/15" />
                  )}
                  <div>
                    <div
                      className="font-serif text-[#BF9448] font-semibold leading-none"
                      style={{ fontSize: 'clamp(1.4rem, 2.5vw, 2rem)' }}
                    >
                      {stat.value}
                    </div>
                    <div className="font-sans text-white/40 text-[10px] uppercase tracking-[0.18em] mt-1.5">
                      {stat.label}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Angled bottom transition */}
      <div
        className="absolute bottom-0 left-0 w-full h-24 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, #0A0806)' }}
      />
    </section>
  );
}
